import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Lock, Plus, Search, X, Activity, TrendingUp, CheckCircle, AlertTriangle, Filter, ChevronDown, ChevronUp, Download, Upload, FileText, FileSpreadsheet, FileOutput, List, LayoutList, LayoutGrid, Shield, Layers, RefreshCcw, Calendar, Clock, XCircle } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getClotures,
  createCloture,
  updateCloture,
  deleteCloture } from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel, dispatchFiscalYearRefresh } from './utils/fiscalYearUtils';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import {
  EMPTY_CLOTURE_FORM,
  recordToFormData,
  buildCloturePayload } from './13-cloture-comptable/cloturePayload';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';

const STATUT_LABEL_KEYS = {
  'En cours': 'statut_in_progress',
  'Provisoire': 'statut_provisional',
  'Définitive': 'statut_definitive',
  'Verrouillée': 'statut_locked',
  'Archivée': 'statut_archived' };

const TYPE_LABEL_KEYS = {
  'Mensuelle': 'type_monthly',
  'Trimestrielle': 'type_quarterly',
  'Semestrielle': 'type_semiannual',
  'Annuelle': 'type_annual' };

const DISTRIBUTION_STATUT_MAP = {
  'Verrouillées': 'Verrouillée',
  'Définitives': 'Définitive',
  'Provisoires': 'Provisoire',
  'En cours': 'En cours' };

const CLOTURE_LIST_COLUMNS = [
  { key: 'cloture_ref', labelKey: 'col_reference' },
  { key: 'exercice_fiscal', labelKey: 'col_exercise' },
  { key: 'type_cloture', labelKey: 'col_type' },
  { key: 'date_fin_periode', labelKey: 'col_end_date' },
  { key: 'progression_pourcent', labelKey: 'col_progress' },
  { key: 'statut_cloture', labelKey: 'col_status' },
];

const ClotureComptablePage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.clotureComptablePage.${key}`, opts);

  const getStatutLabel = (statut) => (statut && STATUT_LABEL_KEYS[statut] ? p(STATUT_LABEL_KEYS[statut]) : statut || p('na'));
  const getTypeLabel = (type) => (type && TYPE_LABEL_KEYS[type] ? p(TYPE_LABEL_KEYS[type]) : type || p('na'));

  // ===== STATE MANAGEMENT =====
  const [clotures, setClotures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCloture, setSelectedCloture] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Advanced Options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_clotures');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Date range filter
  const [setDateRange] = useState({ start: null, end: null });
  
  // Dropdowns
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Selection & Sorting
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('cloture_ref', (row) => row.cloture_ref ?? ''),
    textColumnRule('date_fin_periode', (row) => row.date_fin_periode ?? ''),
    textColumnRule('progression_pourcent', (row) => String(row.progression_pourcent ?? '')),
  ], []);
  
  // Expanded sections for GPS-style collapsible form (FIRST SECTION EXPANDED)
  const [clotureComptableFormTab, setClotureComptableFormTab] = useState('informationsCloture');
  const clotureComptableFormTabs = useMemo(() => ([
    { id: 'informationsCloture', label: p('section_info'), icon: ChevronUp },
    { id: 'validationsArchivage', label: p('section_validations'), icon: ChevronUp },
  ]), [p]);

  
  const [formData, setFormData] = useState({ ...EMPTY_CLOTURE_FORM });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadClotures();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Sorting handler
  // Select handlers
  
  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteCloture(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'إقفالات محاسبية' }));
        setSelectedRecords([]);
        clearSelection();
        loadClotures();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };

  const loadClotures = async () => {
    try {
      setLoading(true);
      const data = await getClotures();
      setClotures(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading clotures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = buildCloturePayload(formData);

      if (selectedCloture) {
        await updateCloture(selectedCloture.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الإقفال المحاسبي' }));
      } else {
        await createCloture(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الإقفال المحاسبي' }));
      }
      await loadClotures();
      dispatchFiscalYearRefresh();
      resetForm();
    } catch (error) {
      console.error('Error saving cloture:', error);
      notifyError(showNotify, p('save_error', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cloture) => {
    setSelectedCloture(cloture);
    setFormData(recordToFormData(cloture));
    setClotureComptableFormTab('informationsCloture');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await deleteCloture(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الإقفال المحاسبي' }));
        await loadClotures();
      } catch (error) {
        console.error('Error deleting cloture:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (cloture) => {
    setSelectedCloture(cloture);
    setShowViewModal(true);
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const cloture = clotures.find((c) => c.id === recordId);
      if (!cloture) return;
      await updateCloture(recordId, { ...cloture, statut_cloture: newStatus });
      await loadClotures();
    } catch (error) {
      console.error('Error updating cloture status:', error);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_CLOTURE_FORM });
    setSelectedCloture(null);
    setShowModal(false);
    setClotureComptableFormTab('informationsCloture');
  };

  const openCreateModal = () => {
    setSelectedCloture(null);
    setFormData({
      ...EMPTY_CLOTURE_FORM,
      exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    });
    setClotureComptableFormTab('informationsCloture');
    setShowModal(true);
  };

  // Stats
  const stats = {
    total: clotures.length,
    enCours: clotures.filter(c => c.statut_cloture === 'En cours').length,
    verrouillees: clotures.filter(c => c.statut_cloture === 'Verrouillée').length,
    progressionMoy: clotures.length > 0 
      ? (clotures.reduce((sum, c) => sum + (parseFloat(c.progression_pourcent) || 0), 0) / clotures.length).toFixed(1)
      : 0,
    anomalies: clotures.reduce((sum, c) => sum + (parseInt(c.anomalies_ouvertes) || 0), 0)
  };

  // Distribution data structure - GPS pattern
  const clotureDistribution = {
    all_clotures: {
      titleKey: 'dist_overview',
      data: [
        {
          nameKey: 'dist_in_progress',
          filterValue: 'En cours',
          count: stats.enCours,
          color: 'bg-gradient-to-r from-amber-500 to-amber-600'
        },
        {
          nameKey: 'dist_locked_plural',
          filterValue: 'Verrouillées',
          count: stats.verrouillees,
          color: 'bg-gradient-to-r from-green-500 to-green-600'
        },
        {
          nameKey: 'dist_definitive_plural',
          filterValue: 'Définitives',
          count: clotures.filter(c => c.statut_cloture === 'Définitive').length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600'
        },
        {
          nameKey: 'dist_provisional_plural',
          filterValue: 'Provisoires',
          count: clotures.filter(c => c.statut_cloture === 'Provisoire').length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600'
        }
      ]
    },
    by_statut: {
      titleKey: 'dist_by_status',
      data: [
        {
          nameKey: 'dist_in_progress',
          filterValue: 'En cours',
          count: stats.enCours,
          color: 'bg-gradient-to-r from-amber-500 to-amber-600'
        },
        {
          nameKey: 'dist_provisional',
          filterValue: 'Provisoire',
          count: clotures.filter(c => c.statut_cloture === 'Provisoire').length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600'
        },
        {
          nameKey: 'dist_definitive',
          filterValue: 'Définitive',
          count: clotures.filter(c => c.statut_cloture === 'Définitive').length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600'
        },
        {
          nameKey: 'dist_locked',
          filterValue: 'Verrouillée',
          count: stats.verrouillees,
          color: 'bg-gradient-to-r from-green-500 to-green-600'
        }
      ]
    },
    by_type: {
      titleKey: 'dist_by_type',
      data: [
        {
          nameKey: 'dist_monthly',
          filterValue: 'Mensuelle',
          count: clotures.filter(c => c.type_cloture === 'Mensuelle').length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600'
        },
        {
          nameKey: 'dist_quarterly',
          filterValue: 'Trimestrielle',
          count: clotures.filter(c => c.type_cloture === 'Trimestrielle').length,
          color: 'bg-gradient-to-r from-green-500 to-green-600'
        },
        {
          nameKey: 'dist_semiannual',
          filterValue: 'Semestrielle',
          count: clotures.filter(c => c.type_cloture === 'Semestrielle').length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600'
        },
        {
          nameKey: 'dist_annual',
          filterValue: 'Annuelle',
          count: clotures.filter(c => c.type_cloture === 'Annuelle').length,
          color: 'bg-gradient-to-r from-red-500 to-red-600'
        }
      ]
    }
  };

  const distributionInfo = clotureDistribution[mainViewMode] || clotureDistribution.all_clotures;

  // Main view tabs - GPS pattern
  const mainTabs = [
    { id: 'all_clotures', labelKey: 'tab_overview', icon: LayoutGrid },
    { id: 'by_statut', labelKey: 'tab_by_status', icon: Shield },
    { id: 'by_type', labelKey: 'tab_by_type', icon: Layers }
  ];

  // Quick filter buttons - GPS pattern
  const quickFilterButtons = [
    { id: 'all', labelKey: 'filter_all', icon: Layers },
    { id: 'En cours', labelKey: 'filter_in_progress', icon: Activity },
    { id: 'Définitive', labelKey: 'filter_definitive', icon: CheckCircle },
    { id: 'Verrouillée', labelKey: 'filter_locked', icon: Lock }
  ];

  const typeOptions = useMemo(() => [
    { value: 'Mensuelle', label: p('type_monthly'), isDefault: true },
    { value: 'Trimestrielle', label: p('type_quarterly'), isDefault: true },
    { value: 'Semestrielle', label: p('type_semiannual'), isDefault: true },
    { value: 'Annuelle', label: p('type_annual'), isDefault: true },
  ], [t]);

  const statutOptions = useMemo(() => [
    { value: 'En cours', label: p('statut_in_progress'), isDefault: true },
    { value: 'Provisoire', label: p('statut_provisional'), isDefault: true },
    { value: 'Définitive', label: p('statut_definitive'), isDefault: true },
    { value: 'Verrouillée', label: p('statut_locked'), isDefault: true },
    { value: 'Archivée', label: p('statut_archived'), isDefault: true },
  ], [t]);

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 'En cours', labelKey: 'statut_in_progress', icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'Provisoire', labelKey: 'statut_provisional', icon: AlertTriangle, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'Définitive', labelKey: 'statut_definitive', icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'Verrouillée', labelKey: 'statut_locked', icon: Lock, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'Archivée', labelKey: 'statut_archived', icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  ], p), [t]);

  const yesNoOptions = useMemo(() => [
    { value: '0', label: p('option_no'), isDefault: true },
    { value: '1', label: p('option_yes'), isDefault: true },
  ], [t]);

  const dateLocale = i18n.language || 'fr';

  const filteredClotures = useMemo(() => {
    const base = clotures.filter(cloture => {

      const matchesFilter = quickFilter === 'all' || cloture.statut_cloture === quickFilter;

      if (activeDistributionFilter) {
        if (mainViewMode === 'by_statut') {
          if (cloture.statut_cloture !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'by_type') {
          if (cloture.type_cloture !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'all_clotures') {
          const expectedStatut = DISTRIBUTION_STATUT_MAP[activeDistributionFilter] || activeDistributionFilter;
          if (cloture.statut_cloture !== expectedStatut) return false;
        }
      }

      const matchesSearch = !searchTerm ||
        cloture.cloture_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cloture.exercice_fiscal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cloture.periode_cloture?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesFilter && matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [clotures, quickFilter, activeDistributionFilter, mainViewMode, searchTerm, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredClotures, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(CLOTURE_LIST_COLUMNS, p);
  const clotureTableColSpan = visibleDataColumnCount + 2;

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header - GPS Pattern */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Lock className="h-8 w-8 text-red-400" />
            {p('title')}
            <button onClick={loadClotures} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={openCreateModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('add_button')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - GPS Pattern with RED GRADIENT for Lock icon */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { labelKey: 'stat_total', value: stats.total, icon: Lock, color: 'from-red-500 to-red-700' },
          { labelKey: 'stat_in_progress', value: stats.enCours, icon: Activity, color: 'from-amber-500 to-amber-600' },
          { labelKey: 'stat_locked', value: stats.verrouillees, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { labelKey: 'stat_avg_progress', value: `${stats.progressionMoy}%`, icon: TrendingUp, color: 'from-cyan-500 to-cyan-600' },
          { labelKey: 'stat_anomalies', value: stats.anomalies, icon: AlertTriangle, color: 'from-red-500 to-red-600' }
        ].slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.labelKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} style={{ pointerEvents: 'none' }}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options Section - GPS Pattern */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            {/* Main View Mode Tabs */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view_label')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setMainViewMode(tab.id); setActiveDistributionFilter(null); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {p(tab.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Filters */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters_label')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map(btn => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {p(btn.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Distribution Overview */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white/80 text-sm font-semibold uppercase tracking-wider">{p(distributionInfo.titleKey)}</h3>
                {activeDistributionFilter && (
                  <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                    <XCircle size={14} />{p('reset_filter')}
                  </button>
                )}
              </div>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                  <button key={item.nameKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterValue ? null : item.filterValue)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterValue ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`font-medium ${activeDistributionFilter === item.filterValue ? 'text-white' : 'text-white/80'}`}>{p(item.nameKey)}</h4>
                      <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                      <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                        const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                        return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      })()}%` }}></div>
                    </div>
                    <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterValue ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })() })}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - GPS Pattern */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('cloture_ref') ? (
                    <TableColumnHeaderFilter columnKey="cloture_ref" label={p('col_reference')} value={columnFilters.cloture_ref || ''} onChange={(v) => setColumnFilter('cloture_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('exercice_fiscal') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_exercise')}</th>
                  ) : null}
                  {isColVisible('type_cloture') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_type')}</th>
                  ) : null}
                  {isColVisible('date_fin_periode') ? (
                    <TableColumnHeaderFilter columnKey="date_fin_periode" label={p('col_end_date')} value={columnFilters.date_fin_periode || ''} onChange={(v) => setColumnFilter('date_fin_periode', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('progression_pourcent') ? (
                    <TableColumnHeaderFilter columnKey="progression_pourcent" label={p('col_progress')} value={columnFilters.progression_pourcent || ''} onChange={(v) => setColumnFilter('progression_pourcent', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_cloture') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredClotures.length > 0 ? (
                  filteredClotures.map((cloture, index) => (
                    <tr 
                      key={cloture.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(cloture.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(cloture.id)}
                        onChange={() => handleSelectRecord(cloture.id)}
                      />
                      {isColVisible('cloture_ref') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{cloture.cloture_ref || p('na')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{cloture.responsable_cloture || p('no_manager')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('exercice_fiscal') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{cloture.exercice_fiscal || p('na')}</span>
                      </td>
                      ) : null}
                      {isColVisible('type_cloture') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{getTypeLabel(cloture.type_cloture)}</span>
                      </td>
                      ) : null}
                      {isColVisible('date_fin_periode') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {cloture.date_fin_periode ? new Date(cloture.date_fin_periode).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : p('na')}
                          </div>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('progression_pourcent') ? (
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-white/10 h-2 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full"
                              style={{ width: `${cloture.progression_pourcent || 0}%` }}
                            />
                          </div>
                          <span className="text-white font-semibold text-sm">{cloture.progression_pourcent || 0}%</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('statut_cloture') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={cloture.statut_cloture || 'En cours'}
                            recordId={cloture.id}
                            options={statusBadgeOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(cloture)}
                          onEdit={() => handleEdit(cloture)}
                          onDelete={() => handleDelete(cloture.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={clotureTableColSpan} className="text-center py-12 text-white/60">
                      <Lock size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Cards View - GPS Pattern */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredClotures.length > 0 ? (
              filteredClotures.map((cloture, index) => (
                <motion.div
                  key={cloture.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Lock size={20} className="text-red-400" />
                          {cloture.cloture_ref || p('na')}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={cloture.statut_cloture || 'En cours'}
                          recordId={cloture.id}
                          options={statusBadgeOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_exercise')}:</span> {cloture.exercice_fiscal || p('na')}</div>
                            <div><span className="font-medium">{p('card_type')}:</span> {getTypeLabel(cloture.type_cloture)}</div>
                            <div><span className="font-medium">{p('card_period')}:</span> {cloture.periode_cloture || p('na')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_start')}:</span> {cloture.date_debut_periode || p('na')}</div>
                            <div><span className="font-medium">{p('card_end')}:</span> {cloture.date_fin_periode || p('na')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_progress')}</h5>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-white/10 h-3 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full"
                                  style={{ width: `${cloture.progression_pourcent || 0}%` }}
                                />
                              </div>
                              <span className="text-white font-semibold">{cloture.progression_pourcent || 0}%</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_manager')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{cloture.responsable_cloture || p('unassigned')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(cloture)}
                      onEdit={() => handleEdit(cloture)}
                      onDelete={() => handleDelete(cloture.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Lock size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal - GPS Pattern with 4 Collapsible Sections */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'} flex items-center gap-2`}>
                <Lock className="h-6 w-6 text-red-400" />
                {selectedCloture ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="cloture-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedCloture ? p('btn_update') : p('btn_create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {clotureComptableFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = clotureComptableFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setClotureComptableFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="cloture-form" onSubmit={handleSubmit}>
                {clotureComptableFormTab === 'informationsCloture' && (
<div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')}</label>
                            <input
                              type="text"
                              value={formData.cloture_ref}
                              onChange={(e) => setFormData({ ...formData, cloture_ref: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_reference')}
                            />
                          </div>
                          <ComptabiliteFiscalYearField
                            label={p('label_fiscal_year')}
                            value={formData.exercice_fiscal}
                            onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })}
                            placeholder={p('placeholder_fiscal_year')}
                            required
                          />
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_period')}</label>
                            <input
                              type="text"
                              value={formData.periode_cloture}
                              onChange={(e) => setFormData({ ...formData, periode_cloture: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_period')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_closure_type')}</label>
                            <CustomSelect
                              value={formData.type_cloture}
                              onChange={(val) => setFormData({ ...formData, type_cloture: val })}
                              options={typeOptions}
                              placeholder={p('select_placeholder')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_start_date')}</label>
                            <CustomDatePicker
                              value={formData.date_debut_periode}
                              onChange={(dateString) => setFormData({ ...formData, date_debut_periode: dateString })}
                              placeholder={p('select_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_end_date')}</label>
                            <CustomDatePicker
                              value={formData.date_fin_periode}
                              onChange={(dateString) => setFormData({ ...formData, date_fin_periode: dateString })}
                              placeholder={p('select_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                            <CustomSelect
                              value={formData.statut_cloture}
                              onChange={(val) => setFormData({ ...formData, statut_cloture: val })}
                              options={statutOptions}
                              placeholder={p('select_placeholder')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_manager')}</label>
                            <input
                              type="text"
                              value={formData.responsable_cloture}
                              onChange={(e) => setFormData({ ...formData, responsable_cloture: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_manager')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_progress')}</label>
                            <CustomNumberInput
                              step={1}
                              value={formData.progression_pourcent}
                              onChange={(v) => setFormData({ ...formData, progression_pourcent: v })}
                              placeholder={p('placeholder_progress')}
                            />
                          </div>
                  </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_controls_done')}</label>
                            <CustomSelect
                              value={formData.controles_effectues}
                              onChange={(val) => setFormData({ ...formData, controles_effectues: val })}
                              options={yesNoOptions}
                              placeholder={p('select_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_open_anomalies')}</label>
                            <CustomNumberInput
                              step={1}
                              value={formData.anomalies_ouvertes}
                              onChange={(v) => setFormData({ ...formData, anomalies_ouvertes: v })}
                              placeholder={p('placeholder_progress')}
                            />
                          </div>
                      </div>
                    </div>
                </div>
                )}
                {clotureComptableFormTab === 'validationsArchivage' && (
<div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <p className="text-white/60 text-sm md:col-span-3">{p('section_entries_placeholder')}</p>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_validation_accounting')}</label>
                            <CustomSelect
                              value={formData.validation_comptable}
                              onChange={(val) => setFormData({ ...formData, validation_comptable: val })}
                              options={yesNoOptions}
                              placeholder={p('select_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_validation_daf')}</label>
                            <CustomSelect
                              value={formData.validation_daf}
                              onChange={(val) => setFormData({ ...formData, validation_daf: val })}
                              options={yesNoOptions}
                              placeholder={p('select_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_validation_dg')}</label>
                            <CustomSelect
                              value={formData.validation_dg}
                              onChange={(val) => setFormData({ ...formData, validation_dg: val })}
                              options={yesNoOptions}
                              placeholder={p('select_placeholder')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_compliance')}</label>
                            <CustomSelect
                              value={formData.conformite_procedures}
                              onChange={(val) => setFormData({ ...formData, conformite_procedures: val })}
                              options={yesNoOptions}
                              placeholder={p('select_placeholder')}
                            />
                          </div>
                          <div className="">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_notes')}
                            />
                          </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && selectedCloture && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Lock className="h-6 w-6 text-red-400" />
                {p('view_modal_title')}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Section 1: Informations Clôture */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Lock size={18} className="text-sky-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_info')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_reference')}</p>
                    <p className="text-white font-semibold">{selectedCloture.cloture_ref || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_fiscal_year')}</p>
                    <p className="text-white font-semibold">{selectedCloture.exercice_fiscal || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_type')}</p>
                    <p className="text-white font-semibold">{getTypeLabel(selectedCloture.type_cloture)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('col_status')}</p>
                    <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium border ${
                      selectedCloture.statut_cloture === 'Verrouillée' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                      selectedCloture.statut_cloture === 'En cours' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                      'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    }`}>
                      {getStatutLabel(selectedCloture.statut_cloture)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 2: Progression */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <TrendingUp size={18} className="text-cyan-400" />
                  <h3 className="text-lg font-semibold text-white">{p('view_progress')}</h3>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 bg-white/10 h-4 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full"
                      style={{ width: `${selectedCloture.progression_pourcent || 0}%` }}
                    />
                  </div>
                  <span className="text-white font-bold text-xl">{selectedCloture.progression_pourcent || 0}%</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowViewModal(false)}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {p('close')}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default ClotureComptablePage;

